import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cn } from '@/lib/utils';
import CircleIcon from '@/components/icons/circle';
import { cva } from 'class-variance-authority';

// Context for the size
const RadioGroupSizeContext = React.createContext<'small' | 'medium'>('medium');

const radioGroupVariants = cva('flex flex-col gap-2 w-full', {
  variants: {
    size: {
      small: '',
      medium: '',
    },
  },
  defaultVariants: {
    size: 'medium',
  },
});

const radioGroupItemVariants = cva(
  'aspect-square rounded-full border border-grey-300 hover:border-green-500 data-[state=checked]:text-green-500 data-[state=checked]:border-green-500 text-green-500 ring-offset-white focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:border-grey-100',
  {
    variants: {
      size: {
        small: 'size-5',
        medium: 'size-6',
      },
    },
    defaultVariants: {
      size: 'medium',
    },
  }
);

type RadioGroupProps = React.ComponentPropsWithoutRef<
  typeof RadioGroupPrimitive.Root
> & {
  children: React.ReactNode;
  size?: 'small' | 'medium';
};
const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  RadioGroupProps
>(({ className, size = 'medium', children, ...props }, ref) => {
  return (
    <RadioGroupSizeContext.Provider value={size}>
      <RadioGroupPrimitive.Root
        className={cn(radioGroupVariants({ size }), className)}
        {...props}
        ref={ref}
      >
        {children}
      </RadioGroupPrimitive.Root>
    </RadioGroupSizeContext.Provider>
  );
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

type RadioGroupItemProps = React.ComponentPropsWithoutRef<
  typeof RadioGroupPrimitive.Item
>;

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(({ className, value, ...props }, ref) => {
  const size = React.useContext(RadioGroupSizeContext);
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      id={value}
      value={value}
      className={cn(radioGroupItemVariants({ size }), className)}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
        <CircleIcon
          className={cn('fill-green-500 text-current', {
            'size-[0.7rem]': size === 'small',
            'size-3': size === 'medium',
          })}
        />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

type RadioGroupItemLabelProps = {
  htmlFor: string;
  children: React.ReactNode;
  className?: string;
};

const RadioGroupItemLabel: React.FC<RadioGroupItemLabelProps> = ({
  htmlFor,
  children,
  className,
}) => (
  <label htmlFor={htmlFor} className={cn('grow', className)}>
    {children}
  </label>
);

RadioGroupItemLabel.displayName = 'RadioGroupItemLabel';

export { RadioGroup, RadioGroupItem, RadioGroupItemLabel };
